/**
 * One ticket: what it is about, its status and priority, and its notes.
 *
 * The port of `Views/TicketDetailPage.xaml` + `TicketDetailViewModel`. Status is where billing hangs -
 * only a finished ticket's time and charges can be invoiced - so the page states the rule on the
 * ticket rather than only where an invoice is refused, by which time the technician is somewhere
 * else. Three rules from that view model carry over:
 *
 *   * THE HEADER STATES WHAT IS STORED, NOT WHAT IS PICKED. The pickers are an unsaved edit; the
 *     line under the title is the server's copy, and the pending sentence says what saving would
 *     change while it is still unsaved.
 *   * EVERY ANSWER RE-SEEDS THE EDITORS from the server's copy (`apply`), so the pickers show what is
 *     actually stored - including a status the server declined to move.
 *   * NOTES COME BACK ON THE TICKET. The server writes a status change as an attributed internal
 *     note in the same update, so a save re-renders the notes from its answer rather than appending.
 *
 * The status list comes from `rules.ts`, never typed here: the list and its order live in one place,
 * so a status added server-side shows up here (and `rules.test.ts` fails until it does).
 */
import { TextArea, type Container } from 'webwin';
import * as crm from '../api/crm.js';
import { describeError } from '../api/http.js';
import { TicketPriority, TicketSource, TicketStatus, type TicketDto, type TicketNoteDto } from '../contracts.gen.js';
import { join, localDateTime } from '../format.js';
import { ALL_TICKET_STATUSES, BILLING_RULE, isBillable, isFinished, statusLabel } from '../rules.js';
import { CardList } from '../ui/CardList.js';
import type { Card } from '../ui/Card.js';
import { chip } from '../ui/Chip.js';
import { EmptyState } from '../ui/EmptyState.js';
import { link } from '../ui/Link.js';
import { StatusLine } from '../ui/StatusLine.js';
import { button, buttonRow, card, checkLine, comboBox, field, fieldRow, FONT, GAP, h1, h2, hstack, inlineRow, muted, relayout, scrollPage, stackOf, text, value, vstack, wrappingCheck } from '../ui/layout.js';
import type { CrmPage, PageContext } from './page.js';
import { attachmentsSection } from './sections/attachments.js';
import { PRIORITIES, PRIORITY_NAMES } from './sections/recurring.js';

export function ticketDetailPage(): CrmPage {
  return {
    mount(host: Container, ctx: PageContext) {
      const ticketId = Number(ctx.params.id);
      let ticket: TicketDto | null = null;
      let busy = false;

      const status = new StatusLine();

      // ---- header ----
      const back = link('← Tickets', () => ctx.go('/tickets'));
      const title = h1('Loading…');
      const subtitle = muted('');
      const customerLink = link('', () => {
        if (ticket) ctx.go(`/customers/${ticket.customerId}`);
      });
      customerLink.visible = false;
      const header = stackOf(vstack({ spacing: GAP.xs }), back, title, subtitle, customerLink);

      // ---- description ----
      const description = text('');
      const descriptionCard = card({ visible: false });
      descriptionCard.controls.add(description);

      // ---- status ----
      const statusPicker = comboBox<string>(ALL_TICKET_STATUSES.map(statusLabel), {
        selectedIndex: 0,
        onSelectionChanged: () => refreshEditorState()
      });
      const priorityPicker = comboBox<string>([...PRIORITY_NAMES], {
        selectedIndex: 1,
        onSelectionChanged: () => refreshEditorState()
      });
      const saveButton = button('Save', () => void save(pickedStatus()), true);
      const resolveButton = button('Mark resolved', () => void moveTo(TicketStatus.Resolved));
      const reopenButton = button('Reopen', () => void moveTo(TicketStatus.InProgress));
      const pendingNote = muted('', 'warn');
      pendingNote.visible = false;
      const billingNote = muted('');
      const billingLink = link('Log time and charges →', () => {
        if (ticket) ctx.go(`/billing/${ticket.customerId}`);
      });

      const statusCard = card({ visible: false });
      statusCard.controls.add(
        stackOf(
          vstack({ spacing: GAP.md }),
          h2('Status'),
          fieldRow(field('Status', statusPicker), field('Priority', priorityPicker)),
          stackOf(buttonRow(), saveButton, resolveButton, reopenButton),
          pendingNote,
          billingNote,
          billingLink
        )
      );

      // ---- notes ----
      const notesHeading = h2('Notes');
      notesHeading.visible = false;
      const notes = new CardList<TicketNoteDto>({
        key: n => n.id,
        build: n => buildNoteCard(n),
        spacing: GAP.sm
      });
      const notesEmpty = new EmptyState('No notes yet.');

      const noteBox = new TextArea({ height: 96, hAlign: 'stretch', wrap: true, placeholder: 'Add a note…' });
      const addNoteButton = button('Add note', () => void addNote(), true);
      noteBox.on('textchanged', () => refreshEditorState());
      // Public is the default, as it always was; emailing is a separate, deliberate tick, and only a
      // public note may be emailed - a private one is by definition not for the customer.
      const visibilityPicker = comboBox<string>(['Public - the customer may see it', 'Private - staff only'], {
        selectedIndex: 0,
        onSelectionChanged: () => refreshEditorState()
      });
      const emailCheck = wrappingCheck('Email this note to the customer');
      emailCheck.on('change', () => refreshEditorState());
      const emailTarget = muted('');
      const noteCard = card({ visible: false });
      noteCard.controls.add(
        stackOf(
          vstack({ spacing: GAP.md }),
          noteBox,
          field('Visibility', visibilityPicker),
          stackOf(vstack({ spacing: GAP.xs }), checkLine(emailCheck), emailTarget),
          stackOf(buttonRow(), addNoteButton)
        )
      );

      const attachments = attachmentsSection({ ctx, owner: { ticketId } });
      attachments.panel.visible = false;

      const { view, content } = scrollPage();
      content.controls.add(
        stackOf(
          vstack({ spacing: GAP.lg }),
          header,
          status.label,
          descriptionCard,
          statusCard,
          attachments.panel,
          stackOf(vstack({ spacing: GAP.sm }), notesHeading, notesEmpty.panel, notes.panel),
          noteCard
        )
      );
      host.controls.add(view);

      // ---------------------------------------------------------------
      //  Rendering
      // ---------------------------------------------------------------

      function buildNoteCard(n: TicketNoteDto): Card {
        const meta = inlineRow();
        meta.controls.add(value(n.authorName || 'Unknown', { font: FONT.small }));
        meta.controls.add(value(localDateTime(n.createdUtc), { font: FONT.small, role: 'muted' }));
        meta.controls.add(n.isInternal ? chip('private', 'neutral') : chip('public', 'info'));

        const shell = card();
        const body = stackOf(vstack({ spacing: GAP.xs }), meta, text(n.body));
        if (n.emailedTo) body.controls.add(muted(`Emailed to ${n.emailedTo}${n.emailedUtc ? ' · ' + localDateTime(n.emailedUtc) : ''}`, 'ok'));
        else if (n.emailError) body.controls.add(muted(`Not emailed: ${n.emailError}`, 'danger'));
        shell.controls.add(body);
        return shell;
      }

      function pickedStatus(): TicketStatus {
        const i = statusPicker.selectedIndex;
        return ALL_TICKET_STATUSES[Math.max(0, Math.min(i, ALL_TICKET_STATUSES.length - 1))] ?? TicketStatus.Open;
      }

      function pickedPriority(): TicketPriority {
        return PRIORITIES[Math.max(0, priorityPicker.selectedIndex)] ?? TicketPriority.Normal;
      }

      /** Seeds every editor and line from the server's copy. */
      function apply(t: TicketDto): void {
        ticket = t;
        title.text = t.title;
        subtitle.text = join([`#${t.id}`, statusLabel(t.status), PRIORITY_NAMES[t.priority], sourceLabel(t)]);
        description.text = t.description || 'No description.';
        description.role = t.description ? 'text' : 'muted';

        statusPicker.selectedIndex = Math.max(0, ALL_TICKET_STATUSES.indexOf(t.status));
        priorityPicker.selectedIndex = Math.max(0, PRIORITIES.indexOf(t.priority));

        billingNote.text = t.isBillable
          ? 'Time and charges logged against this ticket can go on an invoice.'
          : `${BILLING_RULE} Time and charges logged here stay unbilled until it is.`;

        notes.set(t.notes ?? []);
        notesEmpty.update(t.notes?.length ?? 0);

        descriptionCard.visible = statusCard.visible = noteCard.visible = notesHeading.visible = true;
        refreshEditorState();
        relayout(content);
      }

      /** Buttons and the pending sentence - everything that depends on the picks rather than the record. */
      function refreshEditorState(): void {
        const t = ticket;
        const picked = pickedStatus();
        const changed = t !== null && (picked !== t.status || pickedPriority() !== t.priority);

        saveButton.enabled = t !== null && !busy && changed;
        resolveButton.enabled = t !== null && !busy && !isFinished(t.status);
        reopenButton.enabled = t !== null && !busy && isFinished(t.status);
        addNoteButton.enabled = t !== null && !busy && noteBox.text.trim().length > 0;

        const isPrivate = visibilityPicker.selectedIndex === 1;
        const canEmail = t !== null && !busy && !isPrivate && !!t.contactEmail;
        if (!canEmail && emailCheck.checked) emailCheck.checked = false;
        emailCheck.enabled = canEmail;
        const addLabel = emailCheck.checked ? 'Add and email' : 'Add note';
        if (addNoteButton.text !== addLabel) {
          addNoteButton.text = addLabel;
          relayout(addNoteButton);
        }
        const target = !t
          ? ''
          : !t.contactEmail
            ? "This ticket has no contact address to email. Give the device's user, the reporter or the customer's contact an email address."
            : isPrivate
              ? 'A private note is never emailed.'
              : `Goes to ${t.contactName ? `${t.contactName} <${t.contactEmail}>` : t.contactEmail}. A reply comes back onto this ticket.`;
        if (emailTarget.text !== target) {
          emailTarget.text = target;
          relayout(emailTarget);
        }

        let pending = '';
        if (t && picked !== t.status) {
          if (isBillable(picked)) pending = "Saving this releases the ticket's unbilled time and charges for invoicing.";
          else if (isBillable(t.status)) pending = "Saving this holds the ticket's unbilled time and charges back from invoicing.";
        }
        if (pendingNote.text !== pending) {
          pendingNote.text = pending;
          pendingNote.visible = pending.length > 0;
          relayout(pendingNote);
        }
      }

      function setBusy(value: boolean): void {
        busy = value;
        refreshEditorState();
      }

      // ---------------------------------------------------------------
      //  Actions
      // ---------------------------------------------------------------

      async function save(next: TicketStatus): Promise<void> {
        const t = ticket;
        if (!t || busy) return;
        setBusy(true);
        status.busy('Saving…');
        try {
          const updated = await crm.updateTicket(t.id, {
            title: t.title,
            description: t.description,
            status: next,
            priority: pickedPriority(),
            assigneeUserId: t.assigneeUserId
          });
          if (ctx.cancelled()) return;
          apply(updated);
          status.ok(`Ticket is now ${statusLabel(updated.status)}.`);
        } catch (error) {
          if (!ctx.cancelled()) status.error('Could not save the ticket: ' + describeError(error));
        } finally {
          if (!ctx.cancelled()) setBusy(false);
        }
      }

      /** The one-click paths: straight to the state that lets the work be billed, and back. */
      async function moveTo(next: TicketStatus): Promise<void> {
        statusPicker.selectedIndex = Math.max(0, ALL_TICKET_STATUSES.indexOf(next));
        await save(next);
      }

      async function addNote(): Promise<void> {
        const t = ticket;
        const body = noteBox.text.trim();
        if (!t || busy || !body) return;
        const isInternal = visibilityPicker.selectedIndex === 1;
        const emailCustomer = !isInternal && emailCheck.checked;
        setBusy(true);
        status.busy(emailCustomer ? 'Adding and emailing the note…' : 'Adding the note…');
        try {
          const updated = await crm.addTicketNote(t.id, { body, isInternal, emailCustomer });
          if (ctx.cancelled()) return;
          noteBox.text = '';
          emailCheck.checked = false;
          apply(updated);
          // The note is kept whether or not the email went; say which happened.
          const added = updated.notes.reduce<TicketNoteDto | null>((a, n) => (!a || n.id > a.id ? n : a), null);
          if (!emailCustomer) status.ok(isInternal ? 'Private note added.' : 'Note added.');
          else if (added?.emailedTo) status.ok(`Note added and emailed to ${added.emailedTo}.`);
          else status.error(`Note added, but not emailed: ${added?.emailError ?? 'unknown error'}`);
        } catch (error) {
          if (!ctx.cancelled()) status.error('Could not add the note: ' + describeError(error));
        } finally {
          if (!ctx.cancelled()) setBusy(false);
        }
      }

      // ---------------------------------------------------------------
      //  Loading
      // ---------------------------------------------------------------

      async function load(): Promise<void> {
        if (!Number.isInteger(ticketId) || ticketId <= 0) {
          title.text = 'Ticket not found';
          return;
        }
        try {
          const t = await crm.getTicket(ticketId, ctx.dispose.signal());
          if (ctx.cancelled()) return;
          apply(t);
          attachments.panel.visible = true;
          void attachments.refresh();
          // The customer's name is a convenience; the ticket stands without it.
          try {
            const customer = await crm.getCustomer(t.customerId, ctx.dispose.signal());
            if (ctx.cancelled()) return;
            customerLink.text = `${customer.name} →`;
            customerLink.visible = true;
            relayout(content);
          } catch {
            /* the link stays hidden */
          }
        } catch (error) {
          if (ctx.cancelled()) return;
          title.text = 'Ticket not found';
          status.error(describeError(error));
          relayout(content);
        }
      }

      refreshEditorState();
      void load();
    }
  };
}

/** Only said when the ticket did not come from a person in the CRM - that is the interesting case. */
function sourceLabel(t: TicketDto): string {
  switch (t.source) {
    case TicketSource.Agent:
      return 'raised from the device tray';
    case TicketSource.Email:
      return t.sourceEmail
        ? `emailed in by ${t.sourceName ? `${t.sourceName} <${t.sourceEmail}>` : t.sourceEmail}`
        : 'opened by email';
    case TicketSource.Recurring:
      return 'opened by a recurring ticket';
    default:
      return '';
  }
}
